import {
  AccessLevel,
  APIAssociation,
  APICompany,
  APIContact,
  APIDelivery,
  APIDeliveryDestination,
  APIDeliveryStatus,
  APIDeliveryType,
  APIHeartbeat,
  APIKnowledgeBase,
  APIRule,
  APITaskFilter,
  APIUnit,
  AssistantConfiguration,
  ContactSecrets,
  Delivery,
  Filter,
  PropertyInfo,
  SearchableTask,
  APIACT,
  APIHandoffRule,
} from '@super-software-inc/foundation'
import { CompanyType } from '@super-software-inc/foundation/dist/types/Company'
import { TemporaryFile } from 'components/app/Editor/Editor'
import { TaskFormTask } from 'components/app/TaskForm'
import { FilterInterface, FilterValue } from 'components/app/TaskViewOptions'
import { atom } from 'recoil'
import { MobileOperatingSystem } from 'utils/getMobileOperatingSystem'
import { APIPhoneRoutingRule } from '../api/assistant/phoneRoutingRule'

export const heartbeatAtom = atom<APIHeartbeat>({
  key: 'heartbeat',
  default: {
    latestSystemMessage: undefined,
  },
})

/**
 * This atom is used to cache the download URL of the user's avatar.
 */
// eslint-disable-next-line import/prefer-default-export
export const avatarDownloadURLCacheAtom = atom<{ [key: string]: string }>({
  key: 'avatarDownloadURLCache',
  default: {},
})

export const tempFilesAtom = atom<TemporaryFile[]>({
  key: 'tempFiles',
  default: [],
})

export const taskInitialValuesAtom = atom<Partial<TaskFormTask> | undefined>({
  key: 'taskInitialValues',
  default: undefined,
})

export const taskFilterAtom = atom<FilterInterface>({
  key: 'taskFilter',
  default: {
    type: undefined,
    value: undefined,
  },
})

export const taskFiltersAtom = atom<Filter[]>({
  key: 'taskFilters',
  default: [],
})

export const taskFilterFormAtom = atom<{
  isOpen: boolean
  filters: Filter[]
  id?: string
  name?: string
  icon?: string
  color?: string
  order?: number
}>({
  key: 'taskFilterForm',
  default: {
    isOpen: false,
    filters: [],
  },
})

export const taskFilterSelectOpenAtom = atom<FilterValue | undefined>({
  key: 'taskFilterSelectOpen',
  default: undefined,
})

export const showTaskFormAtom = atom<boolean>({
  key: 'showTaskForm',
  default: false,
})

export const showAnnouncementFormAtom = atom<boolean>({
  key: 'showAnnouncementForm',
  default: false,
})

export const newAnnouncementDescriptionAtom = atom<string>({
  key: 'newAnnouncementDescription',
  default: '',
})

export enum DeliveryFormStage {
  SelectAction = 'select-action',
  MobileCamera = 'mobile-camera',
  Camera = 'camera',
  ContactSelection = 'contact-selection',
  ManualEntry = 'manual-entry',
}

export const deliveryFormAtom = atom<{
  isOpen: boolean
  deliveryFormStage: DeliveryFormStage
  delivery: Partial<Delivery>
  labelDestinationOptions: APIDeliveryDestination[]
}>({
  key: 'deliveryForm',
  default: {
    isOpen: false,
    deliveryFormStage: DeliveryFormStage.SelectAction,
    delivery: {},
    labelDestinationOptions: [],
  },
})

export const deliverySheetAtom = atom<APIDelivery | undefined>({
  key: 'deliverySheet',
  default: undefined,
})

export const deliveriesAtom = atom<APIDelivery[]>({
  key: 'deliveries',
  default: [],
})

export const companyDeliveryTypesAtom = atom<APIDeliveryType[]>({
  key: 'companyDeliveryTypes',
  default: [],
})

export const companyDeliveryStatusesAtom = atom<APIDeliveryStatus[]>({
  key: 'companyDeliveryStatuses',
  default: [],
})

export const showMeetingFormAtom = atom<boolean>({
  key: 'showMeetingForm',
  default: false,
})

export const communicationTabAtom = atom<number>({
  key: 'communicationTab',
  default: 0,
})

export const getDefaultPropertyInfo = (): PropertyInfo => ({
  associationId: '',
  units: [],
  actId: '',
  accessLevel: AccessLevel.NoAccess,
  propertyRelation: null,
  boardTerms: [],
  groups: [],
  title: '',
})

export const getDefaultAPIContact = (): APIContact => ({
  id: '',
  userId: null,
  firstName: '',
  lastName: '',
  email: '',
  phone: {
    type: 'mobile',
    number: '',
  },
  associationIds: [],
  secondaryEmails: [],
  secondaryPhones: [],
  companyId: '',
  notificationSettings: {
    frequency: 'activity',
  },
  propertyInfo: [],
})

export const authenticatedUserAtom = atom<{
  contacts: APIContact[]
  companies: APICompany[]
  selectedContact: APIContact
  selectedCompany: APICompany
  secrets: ContactSecrets[]
  taskFilters: APITaskFilter[]
  acts: APIACT[]
  firebaseToken: string | undefined
}>({
  key: 'authenticatedUser',
  default: {
    companies: [], // all companies that have a contact with the userId
    contacts: [], // all contacts that have a contact with the userId
    selectedCompany: {
      id: '',
      name: '',
      associationIds: [],
      active: true,
      pilot: false,
      primaryContact: null,
      type: CompanyType.PropertyManager,
      createdAt: new Date().getTime(),
      modifiedBy: null,
      shortCode: '',
    },
    selectedContact: getDefaultAPIContact(),
    secrets: [],
    taskFilters: [],
    acts: [],
    firebaseToken: undefined,
  },
})

export const companyAssociationsAtom = atom<{
  associations: APIAssociation[]
  companyId: string
}>({
  key: 'companyAssociations',
  default: {
    associations: [],
    companyId: '',
  },
})

export const profileModalAtom = atom<{
  sidebarIsOpen: boolean
  editProfileIsOpen: boolean
  selectedContact: APIContact
  abandonProfileFormIsOpen: boolean
  mergeContactIsOpen: boolean
  corpFirst: boolean
}>({
  key: 'profileModal',
  default: {
    sidebarIsOpen: false,
    editProfileIsOpen: false,
    selectedContact: getDefaultAPIContact(),
    abandonProfileFormIsOpen: false,
    mergeContactIsOpen: false,
    corpFirst: false, // true = the sidebar should show all units/board terms // false =  just the selected property.
  },
})

export const currentContactTabAtom = atom<number>({
  key: 'currentContactTab',
  default: 0,
})

export const currentAssociationTabAtom = atom<number>({
  key: 'currentAssociationTab',
  default: 0,
})

interface UnitActionsAtomType {
  view: {
    isOpen: boolean
    selectedUnit: APIUnit | undefined
  }
  edit: {
    isOpen: boolean
    selectedUnit: APIUnit | undefined
  }
  delete: {
    isOpen: boolean
    selectedUnit: APIUnit | undefined
  }
}

export const unitActionsAtom = atom<UnitActionsAtomType>({
  key: 'unitActionsAtom',
  default: {
    view: {
      isOpen: false,
      selectedUnit: undefined,
    },
    edit: {
      isOpen: false,
      selectedUnit: undefined,
    },
    delete: {
      isOpen: false,
      selectedUnit: undefined,
    },
  },
})

// string is the associationId
export const companyUnitsAtom = atom<Map<string, APIUnit[]>>({
  key: 'companyUnits',
  default: new Map<string, APIUnit[]>(),
})

export const editingRuleAtom = atom<APIRule>({
  key: 'editingRule',
  default: {} as APIRule,
})

interface RoutingState {
  companySettingsTab: string
  settingsTab: number
}

export const routingStateAtom = atom<RoutingState>({
  key: 'routingState',
  default: {
    settingsTab: 0,
    companySettingsTab: 'ai-agent',
  },
})

export const mobileOperatingSystemAtom = atom<MobileOperatingSystem>({
  key: 'mobileOperatingSystem',
  default: MobileOperatingSystem.Unknown,
})

export const searchableTasksAtom = atom<SearchableTask[]>({
  key: 'searchableTasks',
  default: [],
})

export const tasksCountAtom = atom<number>({
  key: 'tasksCount',
  default: 0,
})

// tracks KB that is being edited or created
export const knowledgeBaseFormAtom = atom<{
  knowledgeBase: Partial<APIKnowledgeBase>
  isOpen: boolean
}>({
  key: 'knowledgeBase',
  default: {
    knowledgeBase: {} as APIKnowledgeBase,
    isOpen: false,
  },
})

export const assistantConfigAtom = atom<Partial<AssistantConfiguration>>({
  key: 'assistantConfig',
  default: {} as Partial<AssistantConfiguration>,
})

export const allKnowledgeBasesAtom = atom<APIKnowledgeBase[]>({
  key: 'allKnowledgeBases',
  default: [],
})

export const allHandoffRulesAtom = atom<APIHandoffRule[]>({
  key: 'allHandoffRules',
  default: [],
})

export const allPhoneRoutingRulesAtom = atom<APIPhoneRoutingRule[]>({
  key: 'allPhoneRoutingRules',
  default: [],
})

export const phoneRoutingRuleFormAtom = atom<{
  phoneRoutingRule: Partial<APIPhoneRoutingRule>
  isOpen: boolean
}>({
  key: 'phoneRoutingRuleForm',
  default: {
    phoneRoutingRule: {},
    isOpen: false,
  },
})

export const handoffRuleFormAtom = atom<{
  handoffRule: Partial<APIHandoffRule>
  isOpen: boolean
}>({
  key: 'handoffRuleForm',
  default: {
    handoffRule: {} as Partial<APIHandoffRule>,
    isOpen: false,
  },
})
